// src/utils/telegram.ts

const TG_ID_KEY = 'telegram_user_id';
const TG_STARTAPP_KEY = 'telegram_startapp_param';

// Helper to access WebApp safely
const getWebApp = () => (window as any).Telegram?.WebApp;

// Helper to save ID to both localStorage and CloudStorage
const persistTelegramId = (id: string) => {
    if (!id) return;

    // 1. Local Storage
    try {
        localStorage.setItem(TG_ID_KEY, id);
    } catch (e) {
        console.warn('Failed to save TG ID to localStorage', e);
    }

    // 2. Cloud Storage (Async, fire-and-forget for now)
    try {
        const cloudStorage = getWebApp()?.CloudStorage;
        if (cloudStorage?.setItem) {
            cloudStorage.setItem(TG_ID_KEY, id, (err: any, _success: boolean | undefined) => {
                if (err) console.warn('CloudStorage setItem error:', err);
                else console.log('Telegram ID saved to CloudStorage');
            });
        }
    } catch (e) {
        console.warn('Failed to access CloudStorage', e);
    }
};

export const initTelegram = async () => {
    const webApp = getWebApp();
    if (!webApp) return;

    // Signal ready
    webApp.ready();
    webApp.expand();

    // Prevent Telegram pull-down gesture from minimizing the mini app.
    try {
        if (typeof webApp.disableVerticalSwipes === 'function') {
            webApp.disableVerticalSwipes();
        }
    } catch (e) {
        console.warn('Failed to disable Telegram vertical swipes', e);
    }

    // Attempt to find ID from various sources
    let foundId: string | null = null;

    // 1. InitData (Best)
    if (webApp.initDataUnsafe?.user?.id) {
        foundId = String(webApp.initDataUnsafe.user.id);
    }

    // 2. Hash Params (Backup)
    if (!foundId && window.location.hash) {
        try {
            const hash = window.location.hash.slice(1);
            const params = new URLSearchParams(hash);
            const tgWebAppData = params.get('tgWebAppData');
            if (tgWebAppData) {
                const searchParams = new URLSearchParams(tgWebAppData);
                const userJson = searchParams.get('user');
                if (userJson) {
                    const user = JSON.parse(decodeURIComponent(userJson));
                    if (user.id) foundId = String(user.id);
                }
            }
        } catch (e) {
            console.warn('Failed to parse hash for TG ID', e);
        }
    }

    // 3. Save if found
    if (foundId) {
        persistTelegramId(foundId);
    }

    // Capture referral/startapp param so onboarding can attach referral metadata.
    try {
        const startParamFromWebApp = webApp.initDataUnsafe?.start_param;
        const startParamFromSearch = new URLSearchParams(window.location.search).get('startapp');
        const startParam = startParamFromWebApp || startParamFromSearch;
        if (startParam) {
            localStorage.setItem(TG_STARTAPP_KEY, String(startParam));
        }
    } catch (e) {
        console.warn('Failed to capture Telegram startapp param', e);
    }

    // 4. If still not found, try to restore from CloudStorage (Async)
    // We only wait for this if we really have nothing else
    if (!localStorage.getItem(TG_ID_KEY)) {
        try {
            const cloudStorage = webApp.CloudStorage;
            if (cloudStorage?.getItem) {
                cloudStorage.getItem(TG_ID_KEY, (err: any, value: string | null) => {
                    if (!err && value) {
                        console.log('Restored TG ID from CloudStorage:', value);
                        localStorage.setItem(TG_ID_KEY, value);
                    }
                });
            }
        } catch (e) {
            console.warn('Failed to restore from CloudStorage', e);
        }
    }
};

export const getTelegramId = (): string | null => {
    // 1. Try Memory/InitData first (Most fresh)
    const webApp = getWebApp();
    if (webApp?.initDataUnsafe?.user?.id) {
        const id = String(webApp.initDataUnsafe.user.id);
        persistTelegramId(id); // Ensure latest is validated
        return id;
    }

    // 2. Try LocalStorage (Persistence)
    return localStorage.getItem(TG_ID_KEY);
};

export const getTelegramStartAppParam = (): string | null => {
    const webApp = getWebApp();
    const fromInitData = webApp?.initDataUnsafe?.start_param;
    if (fromInitData) {
        localStorage.setItem(TG_STARTAPP_KEY, String(fromInitData));
        return String(fromInitData);
    }

    const fromStorage = localStorage.getItem(TG_STARTAPP_KEY);
    return fromStorage || null;
};
